import { Fragment } from "react";
import { debounce } from "lodash-es";
import { Link as RouterLink } from "react-router-dom";
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { ContentFlag } from "~/components/content-flag";
import { AWSOrgSetupGuide } from "~/components/guides/aws/aws-org-setup";
import { FormatRelativeDate } from "~/lib/date";
import { ActionMenu } from "~/components/action-menu";
import { AwsIntegration } from "../AwsIntegrationServerlessPage";
import { CanDelete } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Delete";
import { CanUpdate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Update";
import { useCanGetTokenForIntegration } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/GetTokenForIntegration";
import {
  CanTriggerAction,
  useCanTriggerAction,
} from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/TriggerAction";
import { Flex } from "~/components/Flex";
import { PingIcon } from "~/icons";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

interface AwsAccountHeaderProps {
  scope: SpaceScope | OrganizationScope;
  integrationId: string;
  awsIntegration: AwsIntegration;
  actions: {
    triggerScan: () => void;
    ping: () => void;
    deleteIntegration: () => void;
    forceLambdaUpdate: () => void;
    sendLambdaMetrics: () => void;
    sendDiagnosticsData: () => void;
    cancelScans: () => void;
  };
  mrn: string;
}

export const AwsIntegrationServerlessHeader = ({
  awsIntegration,
  mrn,
  actions,
  scope,
  integrationId,
}: AwsAccountHeaderProps) => {
  const hasIntegrationTriggerActionPermission = useCanTriggerAction();
  const hasIntegrationsTokenPermission = useCanGetTokenForIntegration();

  if (
    awsIntegration.status === "WAITING_FOR_SETUP" &&
    awsIntegration?.configurationOptions?.isOrganization &&
    hasIntegrationsTokenPermission
  ) {
    return (
      <Box>
        <Flex justifyContent="flex-end" mb={2}>
          <CanDelete>
            <IconButton
              size="large"
              sx={{
                mx: 0.5,
                color: (theme) => theme.palette.error.main,
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.error.main, 0.15),
                },
              }}
              onClick={actions.deleteIntegration}
            >
              <DeleteIcon />
            </IconButton>
          </CanDelete>
        </Flex>
        <AWSOrgSetupGuide item={awsIntegration} />
      </Box>
    );
  }

  const formatStatus = (status: string) => {
    if (!status) return null;
    switch (status.toLowerCase()) {
      case "waiting_for_setup":
        return "pending";
      default:
        return status;
    }
  };

  const agentCheckinDate = (timestamp: string | null) => {
    if (timestamp == null || timestamp == "") {
      return "Never";
    }
    return FormatRelativeDate(timestamp);
  };

  const showPing =
    awsIntegration.status !== "WAITING_FOR_SETUP" &&
    awsIntegration.status !== "DELETED";

  const showTrigger =
    (awsIntegration.status === "ACTIVE" ||
      awsIntegration.status === "WARNING" ||
      awsIntegration.status === "ERROR") &&
    hasIntegrationTriggerActionPermission;

  return (
    <Box>
      <Grid container>
        <Grid
          size={{
            xs: 12,
            md: "grow",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              id="aws-integration-name"
              variant="h4"
              fontWeight={700}
              sx={{ display: "inline", pr: 2, overflowWrap: "anywhere" }}
            >
              {awsIntegration.name}
            </Typography>
            <ContentFlag
              flag={formatStatus(awsIntegration.status)}
              color={awsIntegration.status}
            />
          </Box>
          <Grid container columnGap={2}>
            <Grid
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              <PingIcon
                sx={{
                  verticalAlign: "middle",
                  color: "text.secondary",
                }}
              />
              {awsIntegration.lastCheckin && (
                <Typography
                  display="inline"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.75 }}
                >
                  Last ping: {agentCheckinDate(awsIntegration.lastCheckin)}
                </Typography>
              )}
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              {awsIntegration.createdBy?.name != "" && (
                <Fragment>
                  <PersonIcon
                    sx={{
                      fontSize: "inherit",
                      color: "text.secondary",
                      verticalAlign: "middle",
                    }}
                  />
                  <Typography
                    display="inline"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.75 }}
                  >
                    Created by: {awsIntegration.createdBy?.name}
                  </Typography>
                </Fragment>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{
            color: "text.secondary",
            mb: 1.625,
          }}
          size={{
            xs: 12,
            md: 5,
          }}
        >
          <Grid
            display="flex"
            alignItems="center"
            justifyContent="end"
            size="grow"
          >
            <CanUpdate>
              <IconButton
                size="large"
                component={RouterLink}
                to={`/space/integrations/aws/${integrationId}/edit?${scope.params}&integrationMrn=${awsIntegration.mrn}`}
                sx={{ mx: 0.5 }}
              >
                <EditIcon />
              </IconButton>
            </CanUpdate>
            <CanDelete>
              <IconButton
                size="large"
                sx={{
                  mx: 0.5,
                  color: (theme) => theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.error.main, 0.15),
                  },
                }}
                onClick={actions.deleteIntegration}
              >
                <DeleteIcon />
              </IconButton>
            </CanDelete>
            <CanTriggerAction>
              <ActionMenu
                id="aws-account-actions"
                actions={[
                  {
                    label: "Copy MRN",
                    action: () => navigator.clipboard.writeText(`${mrn}`),
                    dataName: "aws-copyMrn",
                  },
                  {
                    label: "Force Lambda update",
                    action: actions.forceLambdaUpdate,
                    dataName: "aws-forceLambdaUpdate",
                  },

                  {
                    label: "Send Lambda metrics",
                    action: actions.sendLambdaMetrics,
                    dataName: "aws-sendLambdaMetrics",
                  },

                  {
                    label: "Send diagnostics data",
                    action: actions.sendDiagnosticsData,
                    dataName: "aws-sendDiagnosticsData",
                  },
                  {
                    label: "Cancel running scans",
                    action: actions.cancelScans,
                    dataName: "aws-cancelScans",
                  },
                ]}
              />
            </CanTriggerAction>
            {/* We only show the divider if anything exists to the right of it */}
            {(showPing || showTrigger) && (
              <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            )}
            {/* Ping only visible when not waiting for setup and not deleted */}
            {showPing && (
              <Tooltip title="Ping" placement="top" arrow>
                <IconButton
                  size="large"
                  onClick={actions.ping}
                  sx={{ mr: 3, ml: 1 }}
                  data-name="aws-ping"
                >
                  <PingIcon />
                </IconButton>
              </Tooltip>
            )}
            {/* Scan Trigger only visible when user has permission and status is active/error/warning */}
            {showTrigger && (
              <Button
                variant="contained"
                color="primary"
                onClick={debounce(actions.triggerScan, 1000)}
                data-name="aws-triggerscan"
                sx={{
                  position: "relative",
                  background:
                    "linear-gradient(133.55deg, #9147FF 0%, #4C35E8 100%)", //<--- palette breaking
                  ml: 1,
                }}
              >
                Scan Now
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
